import type { ZodTypeAny } from 'zod';
import type
{
    AgentToolsCallResult,
    AgentToolsHandler,
    AgentToolsInternal,
    AgentToolsListResult,
    AgentToolsRegistrationLevel,
    AgentToolsRegistrationOptions,
    AgentToolsSessionReader,
    AgentToolsToolRegistrationOptions
} from '#layers/core/app/runtime/types/tools';

interface ToolRegistration
{
    kind: 'tool';
    description: string;
    handler: AgentToolsHandler;
    schema: ZodTypeAny;
    argumentsDescription: string;
    owner?: object;
}

interface TextRegistration
{
    kind: 'prompt' | 'resource';
    description: string;
    handler: AgentToolsHandler;
    owner?: object;
}

// Rozłączna suma zamiast jednego typu z opcjonalnym `schema` - dzięki temu w call() nie ma gałęzi
// "narzędzie bez schematu", która po cichu przepuściłaby wywołanie bez sprawdzenia argumentów
type Registration = ToolRegistration | TextRegistration;

// Kanał, którym handler zgłasza call() niepowodzenie domenowe (np. goto.* - "brak uprawnień",
// "nazwa nie istnieje"), odróżnialne od nieoczekiwanego wyjątku (`handler-error`). Wyjątek typowany
// jest jednoznaczny - w przeciwieństwie do rozpoznawania zwykłego obiektu po polu `ok` nie myli
// odmowy z danymi, w których `ok` mogłoby być częścią treści zwracanej przez zasób
//
// Wolno go rzucać tylko w kodzie należącym do narzędzia, które ten powód zgłasza - w handlerze albo
// w jego prywatnym helperze, wołanym z jednego miejsca. Nie wolno go rzucać w kodzie współdzielonym
// (store, serwis, wspólne narzędzie) - `catch` w call() obejmuje cały stos wywołań handlera, więc
// wołający dostałby odmowę z cudzego powodu i nie odróżniłby jej od świadomej
export class ToolCallFailure extends Error
{
    public readonly reason: string;
    public readonly data?: unknown;

    public constructor(reason: string, message: string, data?: unknown)
    {
        super(message);
        this.reason = reason;
        this.data = data;
    }
}

// Kształt, którym panel odrzuca zapytanie HTTP: zwykły obiekt, nie instancja `Error`
// (`plugins/http.ts`). Bez rozpoznania go `String(error)` dawało wołającemu "[object Object]",
// czyli odmowę bez jednej informacji o przyczynie
interface HttpRejection
{
    code: number;
    message: string;
    data?: { message?: unknown; errors?: Record<string, string[]> } | null;
    inner?: { response?: unknown };
    handled: boolean;
}

// Rozpoznawane po kodzie, komunikacie i `handled`, bo te trzy pola mają oba odrzucenia
// z `plugins/http.ts` - `data` bywa puste, a `inner` nie ma go przy odrzuceniu z przechwytywacza
// udanej odpowiedzi. `handled` odróżnia odpowiedź serwera od zgłoszenia domenowego i od `abort()`
// z `common.ts`, które też mają `code` i `message`, a o HTTP nic nie mówią.
// Instancje `Error` odpadają z góry, bo panel odrzuca wyłącznie zwykłym obiektem, a `DOMException`
// niesie liczbowe `code` (przerwane zapytanie to 20) - bez tego wyjątku wołający dostawałby
// zmyślone "HTTP 20" i zdanie o odpowiedzi serwera, której nie było
function isHttpRejection(value: unknown): value is HttpRejection
{
    if (value === null || typeof value !== 'object' || Array.isArray(value) || value instanceof Error)
    {
        return false;
    }

    const candidate = value as Record<string, unknown>;

    return typeof candidate.code === 'number' && typeof candidate.message === 'string' && typeof candidate.handled === 'boolean';
}

// Zapytanie bez odpowiedzi serwera panel oznacza kodem 500, choć żaden serwer niczego nie odrzucił.
// Wołający ma po czym odróżnić zerwane połączenie od awarii po drugiej stronie, bo poprawia się je
// inaczej: jedno da się powtórzyć, drugiego nie
function rejectionStatus(rejection: HttpRejection): number | string
{
    const inner = rejection.inner;

    if (inner !== null && typeof inner === 'object' && inner.response === undefined)
    {
        return 'network';
    }

    return rejection.code;
}

// Komunikaty walidacji siedzą polami w `data.errors` - tak samo czytają je formularze panelu
// (`$errors.record`). Wymienione wszystkie naraz, bo wołający poprawiający po jednym polu na
// wywołanie zapisywałby formularz tyle razy, ile pól ma błąd
function rejectionFields(rejection: HttpRejection): string
{
    const errors = rejection.data?.errors;

    if (errors === null || errors === undefined || typeof errors !== 'object')
    {
        return '';
    }

    return Object.entries(errors)
        .map(([field, messages]) => `${field}: ${(Array.isArray(messages) ? messages : [messages]).join(' ')}`)
        .join('; ');
}

function describeHttpRejection(rejection: HttpRejection): AgentToolsCallResult
{
    const status = rejectionStatus(rejection);
    const text = rejection.message.trim().length > 0 ? rejection.message.trim() : 'Serwer nie podał powodu.';
    const fields = rejectionFields(rejection);

    const head = status === 'network'
        ? `Zapytanie nie doszło do serwera (network): ${text}`
        : `Serwer odrzucił operację (HTTP ${status}): ${text}`;

    return {
        ok: false,
        reason: 'handler-error',
        message: fields.length > 0 ? `${head} Błędy pól - ${fields}.` : head,
        data: rejection.data?.errors === undefined ? { status } : { status, errors: rejection.data.errors }
    };
}

// Wartość rzucona czymkolwiek innym niż `Error`. Zwykły obiekt zamieniony na tekst daje
// "[object Object]", czyli komunikat, z którym wołający nie ma co zrobić - stąd zapis JSON-em
function describeThrown(error: unknown): string
{
    if (error instanceof Error)
    {
        return error.message;
    }

    const text = String(error);

    if (text !== '[object Object]')
    {
        return text;
    }

    try
    {
        return JSON.stringify(error) ?? text;
    }
    catch
    {
        return text;
    }
}

// Obszar domyślny. Widok, który ogłasza jedną listę albo jeden formularz, nie podaje obszaru wcale,
// a jego nazwy narzędzi wyglądają wtedy dokładnie tak, jak wyglądały przed wprowadzeniem obszarów -
// nazwa `default` nie pojawia się ani w nazwie wpisu, ani w wywołaniu, ani w prompcie
export const AGENT_TOOL_DEFAULT_AREA = 'default';

// Nazwa obszaru bez kropki i bez nawiasu. Kropka dlatego, że przestrzeń odcinana jest od reszty
// PIERWSZĄ kropką w nazwie, a nawias dlatego, że koniec obszaru rozpoznajemy po domknięciu członu -
// jedno i drugie rozbiłoby rozbiór nazwy. Wymuszane przy rejestracji, nie przy wywołaniu: nazwa,
// której nie da się rozebrać, jest pomyłką w kodzie widoku i ma wywrócić jego montowanie, a nie
// odmawiać agentowi dopiero wtedy, gdy ten spróbuje jej użyć
const AREA_PATTERN = /^[A-Za-z0-9_-]+$/;

export interface AgentToolName
{
    namespace: string;
    area: string;
    tool: string;
}

// Przestrzeń nazw jest segmentem przed pierwszą kropką, a nazwą właściwą cała reszta - dlatego
// `roles.form` i `roles.form.set` należą do tej samej przestrzeni `roles`. Rejestracja bez kropki
// nie ma przestrzeni, więc gwarancja "kolizja tylko w tej samej przestrzeni" nie miałaby czego pilnować
//
// Przestrzeń wolno domknąć nazwą obszaru w nawiasie kwadratowym (`list[devices].rows`): przestrzenią
// jest wtedy `list`, a `devices` samym obszarem. Bez tego rozbioru komunikat o kolizji mówiłby
// o przestrzeni sklejonej z obszarem, a dwa obszary tej samej przestrzeni nie miałyby jak się spotkać
export function parseAgentToolName(name: string): AgentToolName
{
    const separator = name.indexOf('.');

    if (separator <= 0 || separator === name.length - 1)
    {
        throw new Error(`Nazwa rejestracji musi mieć postać "przestrzen.nazwa" albo "przestrzen[obszar].nazwa" (otrzymano "${name}").`);
    }

    const head = name.slice(0, separator);
    const tool = name.slice(separator + 1);
    const open = head.indexOf('[');

    if (open < 0)
    {
        if (head.includes(']'))
        {
            throw new Error(`Nazwa obszaru w "${name}" musi stać w nawiasie kwadratowym po przestrzeni - na przykład "list[devices].rows".`);
        }

        return { namespace: head, area: AGENT_TOOL_DEFAULT_AREA, tool };
    }

    const area = head.slice(open + 1, head.length - 1);

    if (open === 0 || !head.endsWith(']') || !AREA_PATTERN.test(area))
    {
        throw new Error(`Nazwa obszaru w "${name}" musi stać w nawiasie kwadratowym po przestrzeni i składać się z liter, cyfr, podkreślenia albo myślnika - na przykład "list[devices].rows".`);
    }

    return { namespace: head.slice(0, open), area, tool };
}

// Nazwa wpisu złożona z przestrzeni, obszaru i nazwy właściwej. Obszar domyślny nie dostaje nawiasu,
// bo to jego zapis panel ma dziś na wszystkich podłączonych trasach i to on ma zostać nietknięty
export function agentToolName(namespace: string, area: string, tool: string): string
{
    return area === AGENT_TOOL_DEFAULT_AREA ? `${namespace}.${tool}` : `${namespace}[${area}].${tool}`;
}

// Obszary ogłoszone w danej przestrzeni przez podane nazwy wpisów, w kolejności ich wystąpienia.
// Liczone z samych nazw, a nie z osobnej ewidencji obszarów: obszar istnieje dokładnie tak długo, jak
// długo stoją jego wpisy, więc druga ewidencja mogłaby się z nimi rozjechać przy zakładce albo oknie
// montowanym warunkowo. Nazwa nierozbieralna rzuca tym samym wyjątkiem co przy rejestracji, bo
// rejestr takiej nazwy nie przyjmuje - wpis o niej nie ma jak stanąć w spisie, z którego to liczymy
export function agentToolAreasOf(names: Iterable<string>, namespace: string): string[]
{
    const areas: string[] = [];

    for (const name of names)
    {
        const parsed = parseAgentToolName(name);

        if (parsed.namespace === namespace && !areas.includes(parsed.area))
        {
            areas.push(parsed.area);
        }
    }

    return areas;
}

// Rdzeń rejestru narzędzi agenta. Zwykła klasa z odczytem sesji wstrzykniętym jako funkcja - bez
// zależności od useNuxtApp/Pinia da się utworzyć i przetestować w projekcie testowym `unit`
// (environment: node, bez Nuxt). Plugin (`plugins/tools.ts`) tylko ją tworzy i wiesza na window.edito
export class AgentToolsRegistry implements AgentToolsInternal
{
    private sessionReader: AgentToolsSessionReader;
    private global = new Map<string, Registration>();
    private ephemeral = new Map<string, Registration>();

    public constructor(sessionReader: AgentToolsSessionReader)
    {
        this.sessionReader = sessionReader;
    }

    private register(name: string, registration: Registration, level: AgentToolsRegistrationLevel): void
    {
        const { namespace } = parseAgentToolName(name);

        // Kolizja liczy się na pełnej nazwie w obu poziomach naraz, nie tylko w poziomie docelowym -
        // inaczej call() nie miałby jak jednoznacznie rozstrzygnąć, którą rejestrację wywołać
        if (this.global.has(name) || this.ephemeral.has(name))
        {
            throw new Error(`Nazwa "${name}" jest już zarejestrowana w przestrzeni "${namespace}".`);
        }

        (level === 'global' ? this.global : this.ephemeral).set(name, registration);
    }

    public registerTool(name: string, description: string, schema: ZodTypeAny, handler: AgentToolsHandler, options: AgentToolsToolRegistrationOptions): void
    {
        // Wymóg schematu musi obowiązywać w czasie działania, bo rejestr wisi na window i rejestruje
        // się do niego kod, którego typy nikt nie sprawdza - sam typ TypeScript to gwarancja kompilacji
        if (schema == null || typeof schema.safeParse !== 'function')
        {
            throw new Error(`Narzędzie "${name}" wymaga schematu argumentów z metodą safeParse.`);
        }

        this.register(name, { kind: 'tool', description, handler, schema, argumentsDescription: options.argumentsDescription, owner: options.owner }, options.level);
    }

    public registerPrompt(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
    {
        this.register(name, { kind: 'prompt', description, handler, owner: options.owner }, options.level);
    }

    public registerResource(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
    {
        this.register(name, { kind: 'resource', description, handler, owner: options.owner }, options.level);
    }

    // Jedna metoda dla wszystkich trzech rodzajów wpisów, bo rejestr trzyma je w jednej przestrzeni
    // nazw i kolizję liczy na pełnej nazwie - sama nazwa wskazuje wpis jednoznacznie, więc podawanie
    // rodzaju byłoby dodatkową okazją do pomyłki bez żadnej informacji w zamian
    //
    // Nazwa, której nie ma, nie jest błędem. Wołającym jest zaczep odmontowania komponentu, a ten nie
    // wie, czy jego rejestracja jeszcze istnieje: poziom ulotny mógł zostać wyczyszczony wcześniej,
    // przy zmianie trasy. Wyjątek wywracałby wtedy odmontowanie za stan normalny, a nie za pomyłkę
    // w kodzie. Z tego samego powodu nazwa nie przechodzi tu przez namespaceOf - nazwa bez kropki po
    // prostu nie może być zarejestrowana, więc jej zdjęcie jest tym samym przypadkiem
    //
    // Zdejmuje wpis z obu poziomów, nie tylko z ulotnego. Skoro kolizja liczy się na pełnej nazwie
    // w obu poziomach naraz, ograniczenie do ulotnego dawałoby stan, w którym wyrejestrowanie
    // wygląda na udane, a nazwa dalej blokuje ponowną rejestrację. Dla poziomu globalnego (dziś tylko
    // narzędzia nawigacyjne rejestrowane raz przez plugin) nie ma to zastosowania, ale brak potrzeby
    // nie jest powodem, żeby zakazywać w kodzie
    public unregister(name: string, owner?: object): void
    {
        for (const level of [this.global, this.ephemeral])
        {
            // Z właścicielem zdejmowany jest tylko własny wpis: Vue montuje nowy widok, zanim odmontuje
            // stary, więc stary zdejmowałby przy odmontowaniu wpisy zarejestrowane właśnie przez nowy
            if (owner === undefined || level.get(name)?.owner === owner)
            {
                level.delete(name);
            }
        }
    }

    // Woła to plugin: przy zmianie trasy i przy przejściu sesji z zalogowanej na niezalogowaną. Metoda
    // jest publiczna, bo plugin jest osobnym plikiem, ale nie należy do kontraktu `AgentTools` - i na
    // window.edito.tools plugin wiesza obiekt zawężony do pięciu metod kontraktu, nie tę instancję
    public clearEphemeral(): void
    {
        this.ephemeral.clear();
    }

    public list(): AgentToolsListResult
    {
        const session = this.sessionReader();
        const result: AgentToolsListResult = { tools: [], prompts: [], resources: [] };

        if (session.loaded !== true || session.authenticated !== true)
        {
            return result;
        }

        for (const registrations of [this.global, this.ephemeral])
        {
            for (const [name, registration] of registrations)
            {
                if (registration.kind === 'tool')
                {
                    result.tools.push({ name, description: registration.description, argumentsDescription: registration.argumentsDescription });
                }
                else if (registration.kind === 'prompt')
                {
                    result.prompts.push({ name, description: registration.description });
                }
                else
                {
                    result.resources.push({ name, description: registration.description });
                }
            }
        }

        return result;
    }

    private areas(namespace: string): string[]
    {
        return agentToolAreasOf([...this.global.keys(), ...this.ephemeral.keys()], namespace);
    }

    // Nazwa, której rejestr nie ma. Rozdzielone na dwa powody, bo to dwie różne pomyłki wołającego:
    // przy `not-found` pomylił narzędzie, a przy `unknown-area` trafił w narzędzie istniejące, tylko
    // wskazał obszar, którego ten widok nie ogłasza - i wtedy da się wymienić te, które ogłasza.
    // Liczone wyłącznie na ścieżce braku, więc wywołanie udane przechodzi tędy tak jak dotąd
    private missing(name: string): AgentToolsCallResult
    {
        const notFound: AgentToolsCallResult = { ok: false, reason: 'not-found', message: `Nie znaleziono rejestracji "${name}".` };

        let parsed: AgentToolName;

        try
        {
            parsed = parseAgentToolName(name);
        }
        catch
        {
            return notFound;
        }

        const areas = this.areas(parsed.namespace);

        // Obszar, który istnieje, a narzędzia o tej nazwie nie ma, jest zwykłym brakiem narzędzia -
        // tak samo jak lista bez filtra, która nie rejestruje `list.filter`
        if (areas.length === 0 || areas.includes(parsed.area))
        {
            return notFound;
        }

        const listed = areas.map(area => area === AGENT_TOOL_DEFAULT_AREA ? 'domyślny (wołany bez nawiasu)' : `"${area}"`).join(', ');
        const asked = parsed.area === AGENT_TOOL_DEFAULT_AREA ? 'obszaru domyślnego' : `obszaru "${parsed.area}"`;

        return {
            ok: false,
            reason: 'unknown-area',
            message: `Widok nie ma ${asked} w przestrzeni "${parsed.namespace}". Dostępne obszary: ${listed}. Nazwa obszaru stoi w nawiasie kwadratowym po przestrzeni - na przykład "${agentToolName(parsed.namespace, areas[0]!, parsed.tool)}".`
        };
    }

    public async call(name: string, args?: unknown): Promise<AgentToolsCallResult>
    {
        const session = this.sessionReader();

        if (session.loaded !== true)
        {
            return { ok: false, reason: 'session-unknown', message: 'Stan sesji nie został jeszcze ustalony.' };
        }

        if (session.authenticated !== true)
        {
            return { ok: false, reason: 'unauthenticated', message: 'Brak zalogowanej sesji.' };
        }

        const registration = this.global.get(name) ?? this.ephemeral.get(name);

        if (!registration)
        {
            return this.missing(name);
        }

        // Schemat argumentów wymagany jest tylko dla narzędzi (registerTool) - prompt i zasób nie
        // dostają argumentów wołającego wcale, więc payload zostaje `undefined` niezależnie od `args`
        let payload: unknown;

        if (registration.kind === 'tool')
        {
            const parsed = registration.schema.safeParse(args);

            if (!parsed.success)
            {
                return { ok: false, reason: 'invalid-arguments', message: parsed.error.message };
            }

            // Handler dostaje wartość po walidacji, nie surowe argumenty - inaczej z.coerce, .default
            // i .transform ze schematu nie miałyby żadnego skutku, a nadmiarowe klucze, które zwykły
            // z.object usuwa przy parsowaniu, trafiałyby do handlera
            payload = parsed.data;
        }

        try
        {
            const data = await registration.handler(payload);

            return { ok: true, data };
        }
        catch (error)
        {
            if (error instanceof ToolCallFailure)
            {
                return { ok: false, reason: error.reason, message: error.message, data: error.data };
            }

            // Kształt panelu rozstrzygany przed ogólnym zapisem, bo tylko z niego da się wyjąć
            // komunikat serwera i kod HTTP. Powód odmowy zostaje ten sam - nowa jest treść i `data`
            if (isHttpRejection(error))
            {
                return describeHttpRejection(error);
            }

            return { ok: false, reason: 'handler-error', message: describeThrown(error) };
        }
    }
}
